import axios from 'axios';

// Self-hosted Fastify backend. Set REACT_APP_API_URL at build time on the VPS;
// defaults to a local dev server.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';

// Photos are stored as relative paths (/uploads/..) served from the API host.
// Resolve them against the API origin; pass through anything already absolute.
export const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
export const mediaUrl = (url?: string): string =>
  !url ? '' : url.startsWith('http') ? url : `${API_ORIGIN}${url}`;

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000, // no cold starts on the self-hosted backend
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response error handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      data: error.response?.data,
    });
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/local', { identifier: email, password });
    return response.data;
  },
};

export const categoryService = {
  getAll: async () => {
    const response = await api.get('/categories');
    return response.data;
  },
  create: async (data: { name: string; description?: string }) => {
    const response = await api.post('/categories', { data });
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/categories/${id}`, { data });
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const itemService = {
  getAll: async () => {
    const response = await api.get('/items?populate=*');
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/items', { data });
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/items/${id}`, { data });
    return response.data;
  },
  delete: async (id: number) => {
    const response = await api.delete(`/items/${id}`);
    return response.data;
  },
  markAsBought: async (id: number, password: string) => {
    const response = await api.post(`/items/${id}/mark-bought`, { password });
    return response.data;
  },
};

export const uploadService = {
  upload: async (file: File): Promise<{ url: string; name: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

export default api;
